'use client';

import type { ReactElement } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Building2, User } from 'lucide-react';

export type ContactType = 'individual' | 'organisation';

interface ContactTypeSelectorProps {
  /**
   * Current contact type
   */
  contactType: ContactType;

  /**
   * Callback when type changes
   */
  onTypeChange: (type: ContactType) => void;

  /**
   * Whether the selector is in read or edit mode
   */
  mode: 'read' | 'edit';

  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;
}

/**
 * ContactTypeSelector - Radio button group for selecting contact type
 *
 * Allows users to choose between Individual and Organisation contact types.
 * In read mode, displays the current type as a label.
 */
export function ContactTypeSelector({
  contactType,
  onTypeChange,
  mode,
  disabled = false
}: ContactTypeSelectorProps): ReactElement {
  if (mode === 'read') {
    // In read mode, just show a subtle indicator of the type
    return (
      <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
        {contactType === 'organisation' ? (
          <>
            <Building2 className="h-3.5 w-3.5" />
            <span>Organisation</span>
          </>
        ) : (
          <>
            <User className="h-3.5 w-3.5" />
            <span>Individual</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <Label className="text-muted-foreground mb-2 block text-sm font-medium">
        Contact Type
      </Label>
      <RadioGroup
        value={contactType}
        onValueChange={(value) => onTypeChange(value as ContactType)}
        className="flex gap-6"
        disabled={disabled}
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="individual" id="contact-type-individual" />
          <Label
            htmlFor="contact-type-individual"
            className="flex cursor-pointer items-center gap-1.5 text-sm font-normal"
          >
            <User className="h-4 w-4" />
            Individual
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="organisation" id="contact-type-organisation" />
          <Label
            htmlFor="contact-type-organisation"
            className="flex cursor-pointer items-center gap-1.5 text-sm font-normal"
          >
            <Building2 className="h-4 w-4" />
            Organisation
          </Label>
        </div>
      </RadioGroup>
    </div>
  );
}
